import {
  Aggregate,
  AggregateComposition,
  BenchmarkAssignment,
  PortfolioEntityRef,
} from '@blk/aw-bff-typescript-models';
import { Origin } from '@blk/awt-aladdinwealthserver';
import { AxiosResponse, CancelToken } from 'axios';
import { AGraphWrappedType, unwrapAGraphResponse } from '../../utils/AGraphWrapper';
import {
  prefixEnumForAGraph,
  prefixEnumsForAGraph,
  transformTimestamps,
  wrapPortfolioEntityRefs,
} from '../../utils/ApiUtils';
import { ResourceList, SearchRequest, SearchResponse } from '../AWBridge';
import {
  AGRAPH_AGGREGATE_POST_ENUM_FIELDS,
  AGRAPH_AGGREGATE_POST_TIMESTAMP_FIELDS,
  AGRAPH_SEARCH_REQUEST_ENUM_PREFIXES,
} from './AGraphConstants';
import { isAGraphEnabled } from './AGraphUtils';
import { awPortfolioApi } from './Base';

const BASE_PATH = 'aggregates';

/**
 * Note that awPortfolioApi handles adding pkg and viewAs params to requests.
 * Swagger https://dev.blackrock.com/awportfolio/swagger-ui.html#/aggregates/listUsingGET
 */
export interface GetAggregateParams {
  allowMissing?: boolean;
  asOf?: string; // analysis date in RFC3339 (ISO-8601) format
  origin: Origin;
  pkg: string;
}

export function getAggregate(
  aggIDTag: string,
  aggIDValue: string,
  params: GetAggregateParams,
  cancelToken?: CancelToken
): Promise<AxiosResponse<Aggregate>> {
  const path = `${BASE_PATH}/${aggIDTag}/${aggIDValue}`;
  return awPortfolioApi.get<Aggregate>(path, { params, cancelToken });
}

/**
 * Get the full Aggregate Composition object for an id. Aggregate Compositon object is needed to make the analyze api call for aggregates
 * Swagger: https://dev.blackrock.com/awportfolio/swagger-ui/index.html#/aggregates/compose
 */
export function getAggregateComposition(
  aggIDTag: string,
  aggIDValue: string,
  params: GetAggregateParams,
  cancelToken?: CancelToken
): Promise<AxiosResponse<AggregateComposition>> {
  const path = `${BASE_PATH}/${aggIDTag}/${aggIDValue}/_compose`;
  return awPortfolioApi.get<AggregateComposition>(path, { params, cancelToken });
}

interface GetAggregatesParams {
  asOf: string; // analysis date in RFC3339 (ISO-8601) format
  cursor?: string;
  ids?: string;
  limit: number;
  origin: Origin;
  pkg: string;
  tag?: string;
}

export function getAggregatesForAccount(params: GetAggregatesParams): Promise<AxiosResponse<ResourceList<Aggregate>>> {
  const path = `${BASE_PATH}/lookup-from-accounts`;

  const updatedParams: Record<string, unknown> = {
    ...params,
    origin: prefixEnumForAGraph(params.origin, 'ORIGIN_', isAGraphEnabled()),
  };

  return awPortfolioApi.get<ResourceList<Aggregate>>(path, {
    params: updatedParams,
  });
}

export async function postSearchAggregates(
  origin: Origin,
  params: SearchRequest,
  pkg: string,
  cancelToken?: CancelToken
): Promise<AxiosResponse<SearchResponse<Aggregate>>> {
  const prefixedOrigin = prefixEnumForAGraph(origin, 'ORIGIN_', isAGraphEnabled());
  const path = `${BASE_PATH}/search?origin=${prefixedOrigin}`;
  const payload = isAGraphEnabled() ? prefixEnumsForAGraph(params, AGRAPH_SEARCH_REQUEST_ENUM_PREFIXES) : params;
  const response = await awPortfolioApi.post<AGraphWrappedType<SearchResponse<Aggregate>, 'result'>>(path, payload, {
    cancelToken,
    params: { pkg },
  });
  return unwrapAGraphResponse(response, 'result');
}

export interface PostCreateAggregateParams {
  asOf: string; // analysis date in RFC3339 (ISO-8601) format
  benchmarkRef?: PortfolioEntityRef;
  currency: string;
  id?: string;
  metadata?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- this is a generic object
    [key: string]: any;
  };
  name?: string | null;
  origin: Origin;
  otherBenchmarks?: BenchmarkAssignment[];
  resourceEntities: PortfolioEntityRef[];
  tag: string;
}

/**
 * POST to /awportfolio/api/aggregates to create a new aggregate
 */
export function postCreateAggregate(
  params: PostCreateAggregateParams,
  cancelToken?: CancelToken
): Promise<AxiosResponse<Aggregate>> {
  let payload = params;
  if (isAGraphEnabled()) {
    payload = transformTimestamps(payload, AGRAPH_AGGREGATE_POST_TIMESTAMP_FIELDS, 'stringify', {
      recursiveFieldPatterns: ['*'],
    });
    payload = prefixEnumsForAGraph(payload, AGRAPH_AGGREGATE_POST_ENUM_FIELDS);
    payload = wrapPortfolioEntityRefs(payload, ['resourceEntities']);
  }
  return awPortfolioApi.post(BASE_PATH, payload, { cancelToken });
}

/**
 * PUT to /awportfolio/api/aggregates to update an aggregate
 */
export function putUpdateAggregate(
  aggregateId: string,
  params: PostCreateAggregateParams,
  cancelToken?: CancelToken
): Promise<AxiosResponse<Aggregate>> {
  if (isAGraphEnabled()) {
    // The PUT endpoint was not migrated to AGraph initially - but the POST does the same thing.
    return postCreateAggregate({ ...params, id: aggregateId }, cancelToken);
  }
  const path = `${BASE_PATH}/${params.tag}/${aggregateId}`;
  return awPortfolioApi.put(path, params, { cancelToken });
}

/**
 * Delete a user aggregate
 */
export function deleteAggregate(
  aggregateId: string,
  params: {
    asOf?: string;
    tag: string;
    origin: Origin;
  },
  cancelToken?: CancelToken
): Promise<AxiosResponse<Aggregate>> {
  const path = `${BASE_PATH}/${params.tag}/${aggregateId}`;
  return awPortfolioApi.delete(path, { params, cancelToken });
}

/**
 * GET /awportfolio/api/aggregates/user-paginate to fetch all user aggregates
 */

type UserAggregatesByCursorResp = {
  items: Aggregate[];
  cursor: string;
};

export interface GetUserAggregatesByPageCursorRequest {
  cancelToken?: CancelToken;
  cursor?: string;
}

export interface GetAllUserAggregatesByPageCursorRequest extends GetUserAggregatesByPageCursorRequest {
  memo: Aggregate[];
}

const getUserAggregatesByPageCursor = async ({ cursor, cancelToken }: GetUserAggregatesByPageCursorRequest) => {
  const path = `${BASE_PATH}/user-paginate`;
  const response = await awPortfolioApi.get<AGraphWrappedType<UserAggregatesByCursorResp, 'paginatedOutput'>>(path, {
    params: { cursor },
    cancelToken,
  });
  return unwrapAGraphResponse(response, 'paginatedOutput');
};

export const getAllUserAggregatesByPageCursor = async ({
  memo,
  cursor,
  cancelToken,
}: GetAllUserAggregatesByPageCursorRequest): Promise<Aggregate[]> => {
  const {
    data: { items: userAggregates, cursor: nextCursor },
  } = await getUserAggregatesByPageCursor({ cursor, cancelToken });
  if (userAggregates) {
    const newUserAggregates = memo.concat(userAggregates);
    if (nextCursor) {
      return getAllUserAggregatesByPageCursor({
        memo: newUserAggregates,
        cursor: nextCursor,
        cancelToken,
      });
    }
    return newUserAggregates;
  }
  return memo;
};

export interface EntitlementStatus {
  accountRef: {
    id: string;
    tag: string;
  };
  isEntitled: boolean;
}
export async function getEntitlementStatus({
  tag,
  id,
  pkg,
  cancelToken,
  asOf,
}: {
  tag: string;
  id: string;
  pkg: string;
  cancelToken?: CancelToken;
  asOf?: string; // analysis date in RFC3339 (ISO-8601) format
}): Promise<AxiosResponse<EntitlementStatus[]>> {
  const path = `${BASE_PATH}/${tag}/${id}/_entitlement-status`;
  const response = await awPortfolioApi.get<AGraphWrappedType<EntitlementStatus[], 'entitlementStatuses'>>(path, {
    cancelToken,
    params: { asOf, pkg },
  });
  return unwrapAGraphResponse(response, 'entitlementStatuses');
}
